import { SVLoader } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import TransactionRequestCancelButton from '@/components/transactionRequests/TransactionRequestCancelButton';
import TransactionRequestDetailsCard from '@/components/transactionRequests/TransactionRequestDetailsCard';
import TransactionRequestInfoCard from '@/components/transactionRequests/TransactionRequestInfoCard';
import { ROUTES } from '@/constants/routes';
import { useGetBankAccountDetails } from '@/hooks/requests/useBankAccounts';
import { useGetTransactionRequestsDetails } from '@/hooks/requests/useTransactionRequests';
import { TRANSACTION_REQUEST_STATUS } from '@/types/transactionRequests';
import { generateBankAccountDisplayName } from '@/utils/bankAccounts';
import { Card, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

export default function TransactionRequestDetailsPage() {
  const { bankAccountId = '', uuid = '' } = useParams();
  const { accountDetails, accountsDetailsIsPending } =
    useGetBankAccountDetails(bankAccountId);
  const { transactionRequestDetails, isTransactionRequestDetailsPending } =
    useGetTransactionRequestsDetails(uuid);

  if (accountsDetailsIsPending || isTransactionRequestDetailsPending) {
    return (
      <DefaultLayout header="Transaction Request Details">
        <SVLoader />
      </DefaultLayout>
    );
  }

  if (!accountDetails || !transactionRequestDetails) {
    return (
      <DefaultLayout header="Transaction Request Details">
        <Card>
          <Typography>
            Something went wrong retreiving the bank account transaction
            request. Our team has been notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const { applicationType, business, primaryOwner, owners } = accountDetails;
  const { fullName, email } = primaryOwner.profile;
  const { name: businessName } = business || {};
  const totalOwners = owners.length;
  const bankAccountName = generateBankAccountDisplayName({
    applicationType,
    businessName,
    fullName,
    email,
    totalOwners,
  });
  const { status, description } = transactionRequestDetails;

  return (
    <DefaultLayout
      header="Transaction Request"
      breadcrumb={[
        { label: 'Bank Accounts', href: ROUTES.BANK_ACCOUNTS },
        {
          label: bankAccountName,
          href: `${ROUTES.BANK_ACCOUNTS}/${bankAccountId}`,
        },
        {
          label: 'Transactions Requests',
          href: `${ROUTES.BANK_ACCOUNTS}/${bankAccountId}${ROUTES.TRANSACTION_REQUESTS}`,
        },
        { label: description, isActive: true },
      ]}
      buttons={
        status === TRANSACTION_REQUEST_STATUS.Pending
          ? [<TransactionRequestCancelButton key="cancel" uuid={uuid} />]
          : []
      }>
      <Stack spacing={2}>
        <TransactionRequestDetailsCard
          transactionRequestDetails={transactionRequestDetails}
        />
        <TransactionRequestInfoCard
          transactionRequestDetails={transactionRequestDetails}
        />
      </Stack>
    </DefaultLayout>
  );
}
